import { useState, useEffect, useCallback, useRef } from 'react';
import { Connection, PublicKey } from '@solana/web3.js';
import { SolanaService } from '../services/solanaService';
import { secureStorage } from '../utils/security';
import { getHeliusService } from '../services/heliusService';
// PostType not needed for this implementation
// useService removed - working directly with wallet

const SOLANA_RPC_URL = process.env.REACT_APP_SOLANA_RPC_URL || 'https://api.devnet.solana.com';

export const useZnoospaceProgram = (walletAddress = null) => {
  const [wallet, setWallet] = useState(null);
  const [solanaService, setSolanaService] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Remove dependency on service factory - work directly with wallet
  const [connection] = useState(() => new Connection(SOLANA_RPC_URL, 'confirmed'));
  
  // Guard: only re-initialize when walletAddress actually changes
  const initializedForRef = useRef(null);

  // Initialize wallet and service
  useEffect(() => {
    const initializeWallet = async () => {
      if (walletAddress) {
        // Skip re-initialization if already set up for this address
        if (initializedForRef.current === walletAddress) return;
        
        try {
          console.log('🔧 useZnoospaceProgram: Initializing for wallet:', walletAddress);
          
          // Check for direct Phantom wallet access
          if (window.solana && window.solana.isPhantom && window.solana.publicKey) {
            console.log('✅ useZnoospaceProgram: Direct Phantom wallet detected and connected');
            console.log('🔧 useZnoospaceProgram: Wallet address:', walletAddress);
            
            const walletAdapter = {
              publicKey: window.solana.publicKey,
              connected: window.solana.isConnected,
              signTransaction: window.solana.signTransaction?.bind(window.solana),
              signAllTransactions: window.solana.signAllTransactions?.bind(window.solana),
              walletName: 'Phantom'
            };
            
            setWallet(walletAdapter);
            
            console.log('🚀 useZnoospaceProgram: Creating simple SolanaService...');
            
            // Helper: parse a beam post from a transaction's log messages
            const parseBeamFromTransaction = (txDetails, sigInfo) => {
              if (!txDetails?.meta?.logMessages) return null;
              const logs = txDetails.meta.logMessages;
              const memoLogs = logs.filter(log => log.includes('Memo (len'));
              
              for (const memoLog of memoLogs) {
                if (memoLog.includes('Znoospace DM:') || memoLog.includes('Znoospace Profile:') || memoLog.includes('ChirpX Profile:')) continue;
                
                let memoContent = memoLog.replace(/^Program log: Memo \(len \d+\): "?/, '').replace(/"$/, '');
                
                let postContent = null;
                let postType = null;
                let postData = null;
                
                // Try Znoospace Beam JSON format
                const beamMatch = memoContent.match(/Znoospace Beam: (.+)/);
                if (beamMatch) {
                  try {
                    let jsonStr = beamMatch[1];
                    if (jsonStr.startsWith('"') && jsonStr.endsWith('"')) jsonStr = jsonStr.slice(1, -1);
                    jsonStr = jsonStr.replace(/\\"/g, '"');
                    const beamData = JSON.parse(jsonStr);
                    if (beamData.content) {
                      postContent = beamData.content;
                      postType = 'znoospace_beam';
                      postData = beamData;
                    }
                  } catch (e) { /* parse error, try next method */ }
                }
                
                // Try direct JSON
                if (!postContent && memoContent.includes('"content"')) {
                  try {
                    const jsonMatch = memoContent.match(/\{[^}]*"content"[^}]*\}/);
                    if (jsonMatch) {
                      const jsonData = JSON.parse(jsonMatch[0]);
                      if (jsonData.content) { postContent = jsonData.content; postType = 'json_post'; postData = jsonData; }
                    }
                  } catch (e) { /* parse error */ }
                }
                
                if (!postContent) continue;
                
                // Extract author
                let realAuthor = 'Unknown';
                try {
                  const keys = txDetails.transaction?.message?.accountKeys || txDetails.transaction?.message?.staticAccountKeys || [];
                  if (keys.length > 0) realAuthor = keys[0].toString();
                } catch (e) { /* fallback to Unknown */ }
                
                const authorAddr = postData?.author || realAuthor;
                
                return {
                  id: sigInfo.signature,
                  signature: sigInfo.signature,
                  content: `${postContent}\n\n🔗 View on Solana Explorer: https://explorer.solana.com/tx/${sigInfo.signature}?cluster=devnet`,
                  author: authorAddr,
                  username: `@${authorAddr.slice(0, 8)}...`,
                  displayName: postData?.displayName || `User ${authorAddr.slice(0, 8)}`,
                  timestamp: sigInfo.blockTime * 1000,
                  likes: 0, reposts: 0, replies: 0,
                  txId: sigInfo.signature,
                  type: postType,
                  fee: txDetails.meta?.fee,
                  explorerUrl: `https://explorer.solana.com/tx/${sigInfo.signature}?cluster=devnet`,
                  hasMemo: true
                };
              }
              return null;
            };
            
            // Helper for background beam refresh (used by cache system)
            // Fetches from own wallet + followed wallets for a combined feed
            const heliusFreshFetch = async (addr, lim) => {
              const h = getHeliusService();
              if (h.isAvailable()) {
                // Include followed wallets in background refresh too
                const followKey = `znoospace_following_${addr}`;
                let followed = [];
                try {
                  const raw = secureStorage.getItem(followKey);
                  if (raw) {
                    const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    if (Array.isArray(list)) followed = list;
                  }
                } catch {}
                const allWallets = [addr, ...followed];
                if (allWallets.length > 1) {
                  const r = await h.getGlobalFeed(allWallets, { limit: lim });
                  if (r.beams.length > 0) return r;
                } else {
                  const r = await h.getBeamsForWallet(addr, { limit: lim });
                  if (r.beams.length > 0) return r;
                }
              }
              return null;
            };

            // Create a simple service with blockchain methods
            const simpleService = {
              wallet: walletAdapter,
              connection: connection,

              // Create a post on Solana via memo transaction
              createPost: async (content, mediaUrl = '') => {
                try {
                  const { Transaction, TransactionInstruction, SystemProgram } = await import('@solana/web3.js');
                  
                  const postData = {
                    content: content,
                    mediaUrl: mediaUrl || null,
                    author: walletAdapter.publicKey.toString(),
                    username: `@${walletAdapter.publicKey.toString().slice(0, 8)}...`,
                    displayName: `User ${walletAdapter.publicKey.toString().slice(0, 8)}`,
                    timestamp: new Date().toISOString()
                  };

                  const transaction = new Transaction();
                  const memoInstruction = new TransactionInstruction({
                    keys: [],
                    programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
                    data: Buffer.from(`Znoospace Beam: ${JSON.stringify(postData)}`, 'utf8')
                  });
                  transaction.add(memoInstruction);

                  const { blockhash } = await connection.getLatestBlockhash();
                  transaction.recentBlockhash = blockhash;
                  transaction.feePayer = walletAdapter.publicKey;

                  const signedTx = await walletAdapter.signTransaction(transaction);
                  const signature = await connection.sendRawTransaction(signedTx.serialize());
                  await connection.confirmTransaction(signature, 'confirmed');

                  return {
                    success: true,
                    signature,
                    explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
                  };
                } catch (error) {
                  console.error('❌ createPost error:', error);
                  return { success: false, error: error.message };
                }
              },

              // Create user profile via memo transaction
              createUserProfile: async (username, displayName, bio = '', avatarUrl = '') => {
                try {
                  const { Transaction, TransactionInstruction } = await import('@solana/web3.js');
                  
                  const profileData = {
                    type: 'profile_create',
                    username,
                    displayName: displayName || username,
                    bio: bio || '',
                    avatarUrl: avatarUrl || '',
                    author: walletAdapter.publicKey.toString(),
                    timestamp: new Date().toISOString()
                  };

                  const transaction = new Transaction();
                  const memoInstruction = new TransactionInstruction({
                    keys: [],
                    programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
                    data: Buffer.from(`Znoospace Profile: ${JSON.stringify(profileData)}`, 'utf8')
                  });
                  transaction.add(memoInstruction);

                  const { blockhash } = await connection.getLatestBlockhash();
                  transaction.recentBlockhash = blockhash;
                  transaction.feePayer = walletAdapter.publicKey;

                  const signedTx = await walletAdapter.signTransaction(transaction);
                  const signature = await connection.sendRawTransaction(signedTx.serialize());
                  await connection.confirmTransaction(signature, 'confirmed');

                  return {
                    success: true,
                    signature,
                    profile: profileData,
                    explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
                  };
                } catch (error) {
                  console.error('❌ createUserProfile error:', error);
                  return { success: false, error: error.message };
                }
              },

              // Get posts from a specific user
              // Uses Helius Enhanced API if available, falls back to raw RPC
              getUserPosts: async (userAddress, limit = 20) => {
                try {
                  const addressStr = typeof userAddress === 'string' ? userAddress : userAddress.toString();
                  console.log('🔍 Loading posts for user:', addressStr.slice(0, 8));
                  
                  // ── Helius path (single API call) ──
                  const helius = getHeliusService();
                  if (helius.isAvailable()) {
                    try {
                      const result = await helius.getBeamsForWallet(addressStr, { limit });
                      if (result.beams.length > 0) {
                        console.log(`⚡ Helius: Found ${result.beams.length} posts for user ${addressStr.slice(0, 8)}`);
                        return {
                          success: true,
                          beams: result.beams,
                          posts: result.beams,
                          hasMore: result.hasMore,
                          totalBeams: result.beams.length
                        };
                      }
                      // 0 beams parsed — fall through to RPC
                    } catch (e) {
                      console.warn('⚠️ Helius getUserPosts failed, falling back to RPC:', e.message);
                    }
                  }

                  // ── RPC fallback (N+1 calls) ──
                  const userPubkey = new PublicKey(addressStr);
                  const signatures = await connection.getSignaturesForAddress(userPubkey, { limit: 50 });
                  
                  const candidates = signatures.filter(sig => 
                    sig.memo && (sig.memo.includes('Znoospace Beam') || sig.memo.includes('ChirpX Beam'))
                  );
                  console.log(`📋 Found ${signatures.length} txns, ${candidates.length} are Znoospace beams`);
                  
                  const userPosts = [];
                  for (const sig of candidates.slice(0, limit)) {
                    try {
                      const txDetails = await connection.getTransaction(sig.signature, { maxSupportedTransactionVersion: 0 });
                      if (!txDetails?.meta?.logMessages) continue;
                      
                      const post = parseBeamFromTransaction(txDetails, sig);
                      if (post) userPosts.push(post);
                    } catch (e) {
                      console.warn('⚠️ Error fetching user tx:', e.message);
                    }
                  }
                  
                  console.log('✅ Found', userPosts.length, 'posts for user:', addressStr.slice(0, 8));
                  return {
                    success: true,
                    beams: userPosts,
                    posts: userPosts,
                    hasMore: candidates.length > limit,
                    totalBeams: userPosts.length
                  };
                } catch (error) {
                  console.error('❌ Error getting user posts:', error);
                  return { success: false, beams: [], posts: [], hasMore: false, totalBeams: 0 };
                }
              },

              // Alternative method for querying blockchain posts
              queryZnoospaceBeamsFromBlockchain: async (limit = 20) => {
                try {
                  console.log('🔍 Alternative blockchain query method');
                  // Use the main getBeams method as fallback
                  return await this.getBeams(1, limit);
                } catch (error) {
                  console.error('❌ Error in alternative query method:', error);
                  return { success: false, beams: [], hasMore: false, totalBeams: 0 };
                }
              },

              // Beam cache helpers for instant load on refresh
              _getBeamCache: () => {
                try {
                  const raw = localStorage.getItem('znoospace_beam_cache');
                  if (!raw) return null;
                  const cache = JSON.parse(raw);
                  // Cache valid for 5 minutes AND must match current wallet
                  const currentWallet = walletAdapter.publicKey?.toString();
                  if (cache && cache.ts && Date.now() - cache.ts < 5 * 60 * 1000) {
                    if (cache.wallet && cache.wallet !== currentWallet) {
                      console.log('🗑️ Beam cache is from a different wallet, discarding');
                      localStorage.removeItem('znoospace_beam_cache');
                      return null;
                    }
                    return cache;
                  }
                  return null;
                } catch { return null; }
              },
              _setBeamCache: (beams, nextCursor, hasMore) => {
                try {
                  // Only cache first page, limit to 50 beams to keep localStorage small
                  const toCache = beams.slice(0, 50);
                  localStorage.setItem('znoospace_beam_cache', JSON.stringify({
                    beams: toCache,
                    nextCursor,
                    hasMore,
                    wallet: walletAdapter.publicKey?.toString(),
                    ts: Date.now()
                  }));
                } catch {}
              },

              // Main method to load posts from blockchain with proper pagination
              // Uses Helius Enhanced API if available (single call), falls back to raw RPC
              // onRefresh: optional callback invoked with fresh data after background refresh
              getBeams: async (page = 1, limit = 20, before = null, onRefresh = null) => {
                try {
                  console.log('🔍 Loading beams page:', page, 'limit:', limit, 'before:', before ? before.toString().slice(0, 8) : 'none');
                  
                  const walletAddr = walletAdapter.publicKey.toString();

                  // ── Return cached beams instantly for first page, then refresh in background ──
                  if (page === 1 && !before) {
                    const cached = simpleService._getBeamCache();
                    if (cached && cached.beams.length > 0) {
                      console.log(`⚡ Returning ${cached.beams.length} cached beams (will refresh in background)`);
                      // Schedule a background refresh that ALSO updates React state
                      setTimeout(async () => {
                        try {
                          // Clear Helius in-memory cache to avoid stale empty results
                          try { getHeliusService().clearCache(); } catch {}
                          const fresh = await heliusFreshFetch(walletAddr, limit);
                          if (fresh && fresh.beams.length > 0) {
                            simpleService._setBeamCache(fresh.beams, fresh.nextCursor, fresh.hasMore);
                            // Notify caller with fresh data so React state updates
                            if (onRefresh) {
                              console.log(`🔄 Background refresh: ${fresh.beams.length} fresh beams (was ${cached.beams.length} cached)`);
                              onRefresh({
                                success: true,
                                beams: fresh.beams,
                                hasMore: fresh.hasMore,
                                totalBeams: fresh.beams.length,
                                nextCursor: fresh.nextCursor
                              });
                            }
                          } else {
                            // Fresh fetch returned nothing — clear stale beam cache
                            // so next load does a full fresh fetch instead of returning stale data
                            console.log('⚠️ Background refresh returned 0 beams, clearing stale cache');
                            localStorage.removeItem('znoospace_beam_cache');
                          }
                        } catch {}
                      }, 100);
                      return {
                        success: true,
                        beams: cached.beams,
                        hasMore: cached.hasMore,
                        totalBeams: cached.beams.length,
                        nextCursor: cached.nextCursor
                      };
                    }
                  }

                  // ── Helius Enhanced API path (fast, single call) ──
                  const helius = getHeliusService();
                  if (helius.isAvailable()) {
                    console.log('⚡ Using Helius Enhanced API for beam loading');
                    try {
                      // Build wallet list: own wallet + followed wallets for a combined feed
                      const followedAddresses = simpleService.getFollowing();
                      const allWallets = [walletAddr, ...followedAddresses];
                      console.log(`📡 Fetching beams from ${allWallets.length} wallet(s) (own + ${followedAddresses.length} followed)`);

                      const result = allWallets.length > 1
                        ? await helius.getGlobalFeed(allWallets, { limit })
                        : await helius.getBeamsForWallet(walletAddr, { before, limit });
                      
                      // Only use Helius result if it actually found beams
                      // If it fetched transactions but parsed 0 beams, fall through to RPC
                      // (Helius response format may not match our memo parsing yet)
                      if (result.beams.length > 0) {
                        // Register discovered authors in wallet registry
                        try {
                          const authors = result.beams.map(p => p.author).filter(a => a && a !== 'Unknown');
                          if (authors.length > 0) {
                            const raw = localStorage.getItem('znoospace_known_wallets');
                            const known = raw ? JSON.parse(raw) : [];
                            let changed = false;
                            authors.forEach(a => {
                              if (!known.includes(a)) { known.push(a); changed = true; }
                            });
                            if (changed) localStorage.setItem('znoospace_known_wallets', JSON.stringify(known));
                          }
                        } catch {}
                        console.log(`✅ Helius: Found ${result.beams.length} beams from ${allWallets.length} wallet(s)`);

                        // Cache first page for instant load on next refresh
                        if (page === 1 && !before) {
                          simpleService._setBeamCache(result.beams, result.nextCursor, result.hasMore);
                        }

                        return {
                          success: true,
                          beams: result.beams,
                          hasMore: result.hasMore,
                          totalBeams: result.beams.length,
                          nextCursor: result.nextCursor
                        };
                      } else {
                        console.log('⚠️ Helius returned transactions but 0 parsed beams, falling back to RPC');
                        // Fall through to RPC path
                      }
                    } catch (heliusError) {
                      console.warn('⚠️ Helius API failed, falling back to RPC:', heliusError.message);
                      // Fall through to RPC path below
                    }
                  }

                  // ── Raw RPC fallback path (N+1 calls, slower) ──
                  console.log('🔗 Using raw RPC for beam loading (no Helius API key)');
                  const userPubkey = walletAdapter.publicKey;
                  
                  // Add timeout to prevent hanging
                  const timeoutPromise = new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Blockchain query timeout')), 15000)
                  );
                  
                  // Fetch more signatures than needed so we can filter for beams
                  const fetchLimit = Math.min(limit * 5, 100);
                  const queryOptions = { limit: fetchLimit };
                  if (before && typeof before === 'string') {
                    queryOptions.before = before;
                  }
                  
                  let recentTransactions = [];
                  try {
                    recentTransactions = await Promise.race([
                      connection.getSignaturesForAddress(userPubkey, queryOptions),
                      timeoutPromise
                    ]);
                  } catch (timeoutError) {
                    console.log('⚠️ Blockchain query timed out, returning empty results');
                    return { 
                      success: true, 
                      beams: [], 
                      hasMore: false,
                      totalBeams: 0,
                      nextCursor: null
                    };
                  }
                  
                  console.log('🔍 Found', recentTransactions.length, 'transactions for wallet');
                  
                  // Pre-filter: use the memo field on signature info to find Znoospace beams
                  // This avoids expensive getTransaction calls for non-beam transactions
                  const beamCandidates = recentTransactions.filter(sig => 
                    sig.memo && (sig.memo.includes('Znoospace Beam') || sig.memo.includes('ChirpX Beam'))
                  );
                  console.log(`📋 Pre-filtered: ${beamCandidates.length} beam candidates out of ${recentTransactions.length} transactions`);
                  
                  const blockchainPosts = [];
                  for (const tx of beamCandidates.slice(0, limit)) {
                    try {
                      const txTimeoutPromise = new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('Transaction query timeout')), 3000)
                      );
                      
                      const txDetails = await Promise.race([
                        connection.getTransaction(tx.signature, {
                          maxSupportedTransactionVersion: 0
                        }),
                        txTimeoutPromise
                      ]);
                      
                      const post = parseBeamFromTransaction(txDetails, tx);
                      if (post) {
                        blockchainPosts.push(post);
                        console.log('✅ Parsed beam:', tx.signature.slice(0, 8));
                      }
                    } catch (txError) {
                      console.log('⚠️ Error parsing transaction:', txError.message);
                    }
                  }
                  
                  if (blockchainPosts.length > 0) {
                    console.log('✅ Found', blockchainPosts.length, 'posts from blockchain');
                    // Register discovered authors in the wallet registry
                    try {
                      const authors = blockchainPosts.map(p => p.author).filter(a => a && a !== 'Unknown');
                      if (authors.length > 0) {
                        const raw = localStorage.getItem('znoospace_known_wallets');
                        const known = raw ? JSON.parse(raw) : [];
                        let changed = false;
                        authors.forEach(a => {
                          if (!known.includes(a)) { known.push(a); changed = true; }
                        });
                        if (changed) localStorage.setItem('znoospace_known_wallets', JSON.stringify(known));
                      }
                    } catch {}
                  } else {
                    console.log('📝 No real blockchain posts found - this is expected for a new social network');
                    console.log('💡 To see posts: Create a post using the compose feature, or wait for other users to post');
                  }
                  
                  // Determine if there are more transactions to paginate through
                  const hasMore = recentTransactions.length === fetchLimit;
                  const nextCursor = recentTransactions.length > 0 ? 
                    recentTransactions[recentTransactions.length - 1].signature : null;
                  
                  // Cache first page for instant load on next refresh
                  if (page === 1 && !before && blockchainPosts.length > 0) {
                    simpleService._setBeamCache(blockchainPosts, nextCursor, hasMore);
                  }

                  return { 
                    success: true, 
                    beams: blockchainPosts, 
                    hasMore: hasMore,
                    totalBeams: blockchainPosts.length,
                    nextCursor: nextCursor
                  };
                } catch (error) {
                  console.error('❌ Error in getBeams:', error);
                  return {
                    success: false,
                    beams: [],
                    hasMore: false,
                    totalBeams: 0,
                    error: error.message
                  };
                }
              },

              // Persistent registry of known Znoospace wallets (uses localStorage directly to avoid double-serialization)
              _getKnownWallets: () => {
                try {
                  const raw = localStorage.getItem('znoospace_known_wallets');
                  if (!raw) return [];
                  return JSON.parse(raw);
                } catch { return []; }
              },
              _saveKnownWallets: (wallets) => {
                const unique = [...new Set(wallets)];
                // Cap at 500 entries to prevent localStorage bloat
                const capped = unique.length > 500 ? unique.slice(-500) : unique;
                localStorage.setItem('znoospace_known_wallets', JSON.stringify(capped));
              },
              registerWallets: (addrs) => {
                try {
                  const raw = localStorage.getItem('znoospace_known_wallets');
                  const known = raw ? JSON.parse(raw) : [];
                  let changed = false;
                  addrs.forEach(a => {
                    if (a && a !== 'Unknown' && !known.includes(a)) {
                      known.push(a);
                      changed = true;
                    }
                  });
                  if (changed) localStorage.setItem('znoospace_known_wallets', JSON.stringify(known));
                } catch {}
              },

              // Discover users by scanning known Znoospace wallets + global memo program
              discoverUsers: async () => {
                try {
                  console.log('🌐 Discovering Znoospace users...');
                  
                  // Step 1: Gather known wallet addresses from multiple sources
                  const knownWallets = new Set();
                  
                  // Add current user
                  const myAddr = walletAdapter.publicKey.toString();
                  knownWallets.add(myAddr);
                  
                  // Add wallets from persistent registry (localStorage directly)
                  try {
                    const raw = localStorage.getItem('znoospace_known_wallets');
                    if (raw) {
                      const list = JSON.parse(raw);
                      if (Array.isArray(list)) list.forEach(w => knownWallets.add(w));
                    }
                  } catch {}
                  
                  // Add wallets from follow list
                  try {
                    const raw = secureStorage.getItem(`znoospace_following_${myAddr}`);
                    if (raw) {
                      const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                      if (Array.isArray(list)) list.forEach(w => knownWallets.add(w));
                    }
                  } catch {}
                  
                  // Add wallets from profile index (UserProfileService stores profiles keyed by wallet address)
                  const profileData = {};
                  try {
                    const profileRaw = secureStorage.getItem('znoospace_user_profiles');
                    if (profileRaw) {
                      const profiles = typeof profileRaw === 'string' ? JSON.parse(profileRaw) : profileRaw;
                      if (profiles && typeof profiles === 'object') {
                        Object.keys(profiles).forEach(w => {
                          knownWallets.add(w);
                          profileData[w] = profiles[w];
                        });
                      }
                    }
                  } catch {}
                  
                  // Scan all localStorage keys for wallet-like addresses (profile caches, etc.)
                  try {
                    const walletPattern = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
                    for (let i = 0; i < localStorage.length; i++) {
                      const key = localStorage.key(i);
                      if (!key) continue;
                      // Extract wallet addresses from profile_cache_ keys
                      const cacheMatch = key.match(/profile_cache_([1-9A-HJ-NP-Za-km-z]{32,44})/);
                      if (cacheMatch) knownWallets.add(cacheMatch[1]);
                      const profileMatch = key.match(/profile_([1-9A-HJ-NP-Za-km-z]{32,44})/);
                      if (profileMatch) knownWallets.add(profileMatch[1]);
                    }
                  } catch {}
                  
                  // Add wallets from onboarding state cache
                  try {
                    const onboardingRaw = localStorage.getItem('znoospace_onboarding_state');
                    if (onboardingRaw) {
                      const ob = JSON.parse(onboardingRaw);
                      if (ob?.state?.profile?.author) knownWallets.add(ob.state.profile.author);
                      if (ob?.state?.profile?.walletAddress) knownWallets.add(ob.state.profile.walletAddress);
                    }
                  } catch {}
                  
                  console.log(`📋 Known wallets from all sources: ${knownWallets.size}`, [...knownWallets]);
                  
                  // Step 2: Quick global scan to discover NEW wallets
                  const helius = getHeliusService();
                  if (helius.isAvailable()) {
                    // Helius path: query own wallet for beams to discover co-authors
                    try {
                      console.log('⚡ Using Helius for user discovery');
                      const result = await helius.getBeamsForWallet(walletAdapter.publicKey.toString(), { limit: 50 });
                      if (result.beams) {
                        result.beams.forEach(b => {
                          if (b.author && b.author !== 'Unknown') knownWallets.add(b.author);
                        });
                      }
                    } catch (e) {
                      console.warn('⚠️ Helius discovery failed:', e.message);
                    }
                  } else {
                    // RPC fallback: global memo scan (slower, N+1)
                    try {
                      const MEMO_PROGRAM_ID = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');
                      const globalSigs = await Promise.race([
                        connection.getSignaturesForAddress(MEMO_PROGRAM_ID, { limit: 1000 }),
                        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000))
                      ]);
                      
                      const beamSigs = globalSigs.filter(sig => 
                        sig.memo && (sig.memo.includes('Znoospace Beam') || sig.memo.includes('ChirpX Beam'))
                      );
                      console.log(`🔍 Global scan: ${beamSigs.length} Znoospace beams in ${globalSigs.length} memo txns`);
                      
                      for (const sig of beamSigs.slice(0, 15)) {
                        try {
                          const tx = await Promise.race([
                            connection.getTransaction(sig.signature, { maxSupportedTransactionVersion: 0 }),
                            new Promise((_, reject) => setTimeout(() => reject(), 3000))
                          ]);
                          if (tx) {
                            const keys = tx.transaction?.message?.accountKeys || tx.transaction?.message?.staticAccountKeys || [];
                            if (keys.length > 0) knownWallets.add(keys[0].toString());
                          }
                        } catch {}
                      }
                    } catch (e) {
                      console.warn('⚠️ Global scan failed, using known wallets only');
                    }
                  }
                  
                  // Save updated wallet registry (localStorage directly)
                  localStorage.setItem('znoospace_known_wallets', JSON.stringify([...knownWallets]));
                  console.log(`📋 Total wallets to scan: ${knownWallets.size}`, [...knownWallets]);
                  
                  // Step 3: Pre-populate usersMap from profile data (so users appear even with 0 beams)
                  const allBeams = [];
                  const usersMap = {};
                  
                  Object.entries(profileData).forEach(([addr, profile]) => {
                    if (addr && addr !== 'Unknown') {
                      usersMap[addr] = {
                        address: addr,
                        displayName: profile.displayName || `User ${addr.slice(0, 8)}`,
                        username: profile.username || `@${addr.slice(0, 8)}...`,
                        postCount: 0,
                        totalLikes: 0,
                        latestPost: null,
                        latestTimestamp: profile.lastUpdated || 0,
                      };
                    }
                  });
                  
                  // Scan each known wallet for their Znoospace beams
                  const heliusScan = getHeliusService();
                  
                  if (heliusScan.isAvailable()) {
                    // ── Helius path: parallel queries, no N+1 ──
                    console.log(`⚡ Scanning ${knownWallets.size} wallets via Helius`);
                    const scanResults = await Promise.allSettled(
                      [...knownWallets].map(addr =>
                        heliusScan.getBeamsForWallet(addr, { limit: 5 })
                          .then(result => ({ addr, ...result }))
                      )
                    );
                    
                    for (const result of scanResults) {
                      if (result.status !== 'fulfilled') continue;
                      const { addr, beams } = result.value;
                      if (!beams || beams.length === 0) continue;
                      
                      if (!usersMap[addr]) {
                        usersMap[addr] = {
                          address: addr,
                          displayName: `User ${addr.slice(0, 8)}`,
                          username: `@${addr.slice(0, 8)}...`,
                          postCount: beams.length,
                          totalLikes: 0,
                          latestPost: null,
                          latestTimestamp: 0,
                        };
                      } else {
                        usersMap[addr].postCount = beams.length;
                      }
                      
                      for (const beam of beams) {
                        allBeams.push(beam);
                        if (beam.displayName && !beam.displayName.startsWith('User ')) {
                          usersMap[addr].displayName = beam.displayName;
                        }
                        if (beam.username && !beam.username.includes('...')) {
                          usersMap[addr].username = beam.username;
                        }
                        if (beam.timestamp > (usersMap[addr].latestTimestamp || 0)) {
                          usersMap[addr].latestTimestamp = beam.timestamp;
                          usersMap[addr].latestPost = beam.rawContent || beam.content;
                        }
                        usersMap[addr].totalLikes += (beam.likes || 0);
                      }
                    }
                  } else {
                    // ── RPC fallback: sequential per-wallet scan ──
                    for (const addr of knownWallets) {
                      try {
                        const walletPubkey = new PublicKey(addr);
                        const sigs = await Promise.race([
                          connection.getSignaturesForAddress(walletPubkey, { limit: 30 }),
                          new Promise((_, reject) => setTimeout(() => reject(), 5000))
                        ]);
                        
                        const beamSigs = sigs.filter(s => 
                          s.memo && (s.memo.includes('Znoospace Beam') || s.memo.includes('ChirpX Beam'))
                        );
                        
                        if (beamSigs.length === 0) continue;
                        
                        if (!usersMap[addr]) {
                          usersMap[addr] = {
                            address: addr,
                            displayName: `User ${addr.slice(0, 8)}`,
                            username: `@${addr.slice(0, 8)}...`,
                            postCount: beamSigs.length,
                            totalLikes: 0,
                            latestPost: null,
                            latestTimestamp: beamSigs[0]?.blockTime ? beamSigs[0].blockTime * 1000 : 0,
                          };
                        } else {
                          usersMap[addr].postCount = beamSigs.length;
                        }
                        
                        for (const sig of beamSigs.slice(0, 3)) {
                          try {
                            const tx = await Promise.race([
                              connection.getTransaction(sig.signature, { maxSupportedTransactionVersion: 0 }),
                              new Promise((_, reject) => setTimeout(() => reject(), 3000))
                            ]);
                            const post = parseBeamFromTransaction(tx, sig);
                            if (post) {
                              allBeams.push(post);
                              if (post.displayName && !post.displayName.startsWith('User ')) {
                                usersMap[addr].displayName = post.displayName;
                              }
                              if (post.username && !post.username.includes('...')) {
                                usersMap[addr].username = post.username;
                              }
                              if (post.timestamp > (usersMap[addr].latestTimestamp || 0)) {
                                usersMap[addr].latestTimestamp = post.timestamp;
                                usersMap[addr].latestPost = post.content;
                              }
                              usersMap[addr].totalLikes += (post.likes || 0);
                            }
                          } catch {}
                        }
                        
                        await new Promise(r => setTimeout(r, 200));
                      } catch (e) {
                        // Skip wallets that fail
                      }
                    }
                  }
                  
                  console.log(`✅ Discovered ${Object.keys(usersMap).length} users with ${allBeams.length} beams`);
                  
                  return { 
                    success: true, 
                    users: Object.values(usersMap),
                    beams: allBeams 
                  };
                } catch (error) {
                  console.error('❌ Error discovering users:', error);
                  return { success: false, users: [], beams: [] };
                }
              },

              // ── Follow System (persisted via secureStorage) ──

              // Get the storage key for this wallet's follow list
              _followKey: () => `znoospace_following_${walletAdapter.publicKey.toString()}`,

              // Get list of addresses this user follows
              getFollowing: () => {
                try {
                  const raw = secureStorage.getItem(`znoospace_following_${walletAdapter.publicKey.toString()}`);
                  if (!raw) return [];
                  const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                  return Array.isArray(list) ? list : [];
                } catch (e) {
                  console.warn('⚠️ Error reading follow list:', e.message);
                  return [];
                }
              },

              // Follow a user
              followUser: async (targetAddress) => {
                const addr = typeof targetAddress === 'string' ? targetAddress : targetAddress.toString();
                const key = `znoospace_following_${walletAdapter.publicKey.toString()}`;
                const current = (() => {
                  try {
                    const raw = secureStorage.getItem(key);
                    if (!raw) return [];
                    const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    return Array.isArray(list) ? list : [];
                  } catch { return []; }
                })();
                
                if (current.includes(addr)) {
                  return { success: true, alreadyFollowing: true };
                }
                current.push(addr);
                secureStorage.setItem(key, JSON.stringify(current));
                // Clear beam cache so feed refreshes with new followed user's posts
                try { localStorage.removeItem('znoospace_beam_cache'); } catch {}
                window.dispatchEvent(new Event('znoospace_follow_change'));
                console.log(`✅ Now following ${addr.slice(0, 8)}... (${current.length} total)`);
                return { success: true, followCount: current.length };
              },

              // Unfollow a user
              unfollowUser: async (targetAddress) => {
                const addr = typeof targetAddress === 'string' ? targetAddress : targetAddress.toString();
                const key = `znoospace_following_${walletAdapter.publicKey.toString()}`;
                const current = (() => {
                  try {
                    const raw = secureStorage.getItem(key);
                    if (!raw) return [];
                    const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    return Array.isArray(list) ? list : [];
                  } catch { return []; }
                })();
                
                const updated = current.filter(a => a !== addr);
                secureStorage.setItem(key, JSON.stringify(updated));
                // Clear beam cache so feed refreshes without unfollowed user's posts
                try { localStorage.removeItem('znoospace_beam_cache'); } catch {}
                window.dispatchEvent(new Event('znoospace_follow_change'));
                console.log(`✅ Unfollowed ${addr.slice(0, 8)}... (${updated.length} remaining)`);
                return { success: true, followCount: updated.length };
              },

              // Load posts from all followed users (for Following tab)
              getFollowingFeed: async (limit = 30) => {
                try {
                  const followedAddresses = simpleService.getFollowing();
                  if (!followedAddresses || followedAddresses.length === 0) {
                    console.log('👥 No followed users, returning empty feed');
                    return { success: true, beams: [] };
                  }
                  
                  // Include own wallet + followed wallets
                  const allWallets = [walletAdapter.publicKey.toString(), ...followedAddresses];
                  console.log(`👥 Loading Following feed for ${allWallets.length} wallets...`);
                  
                  const helius = getHeliusService();
                  if (helius.isAvailable()) {
                    const result = await helius.getGlobalFeed(allWallets, { limit });
                    console.log(`✅ Following feed: ${result.beams.length} beams from ${allWallets.length} wallets`);
                    return { success: true, beams: result.beams, hasMore: result.hasMore };
                  } else {
                    console.warn('⚠️ Helius not available for Following feed');
                    return { success: true, beams: [] };
                  }
                } catch (error) {
                  console.error('❌ Error loading Following feed:', error);
                  return { success: false, beams: [], error: error.message };
                }
              },

              // Check if following a specific user
              isFollowing: (targetAddress) => {
                const addr = typeof targetAddress === 'string' ? targetAddress : targetAddress.toString();
                try {
                  const raw = secureStorage.getItem(`znoospace_following_${walletAdapter.publicKey.toString()}`);
                  if (!raw) return { isFollowing: false };
                  const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                  return { isFollowing: Array.isArray(list) && list.includes(addr) };
                } catch {
                  return { isFollowing: false };
                }
              },

              // ── Block System (persisted via secureStorage) ──

              getBlockedUsers: () => {
                try {
                  const raw = secureStorage.getItem(`znoospace_blocked_${walletAdapter.publicKey.toString()}`);
                  if (!raw) return [];
                  const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                  return Array.isArray(list) ? list : [];
                } catch { return []; }
              },

              isBlocked: (targetAddress) => {
                const addr = typeof targetAddress === 'string' ? targetAddress : targetAddress.toString();
                try {
                  const raw = secureStorage.getItem(`znoospace_blocked_${walletAdapter.publicKey.toString()}`);
                  if (!raw) return false;
                  const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                  return Array.isArray(list) && list.includes(addr);
                } catch { return false; }
              },

              blockUser: async (targetAddress) => {
                const addr = typeof targetAddress === 'string' ? targetAddress : targetAddress.toString();
                const key = `znoospace_blocked_${walletAdapter.publicKey.toString()}`;
                const current = (() => {
                  try {
                    const raw = secureStorage.getItem(key);
                    if (!raw) return [];
                    const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    return Array.isArray(list) ? list : [];
                  } catch { return []; }
                })();
                if (current.includes(addr)) return { success: true, alreadyBlocked: true };
                current.push(addr);
                secureStorage.setItem(key, JSON.stringify(current));
                // Also unfollow if following
                try { await simpleService.unfollowUser(addr); } catch {}
                window.dispatchEvent(new Event('znoospace_block_change'));
                console.log(`🚫 Blocked ${addr.slice(0, 8)}... (${current.length} total)`);
                return { success: true, blockCount: current.length };
              },

              unblockUser: async (targetAddress) => {
                const addr = typeof targetAddress === 'string' ? targetAddress : targetAddress.toString();
                const key = `znoospace_blocked_${walletAdapter.publicKey.toString()}`;
                const current = (() => {
                  try {
                    const raw = secureStorage.getItem(key);
                    if (!raw) return [];
                    const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    return Array.isArray(list) ? list : [];
                  } catch { return []; }
                })();
                const updated = current.filter(a => a !== addr);
                secureStorage.setItem(key, JSON.stringify(updated));
                window.dispatchEvent(new Event('znoospace_block_change'));
                console.log(`✅ Unblocked ${addr.slice(0, 8)}... (${updated.length} remaining)`);
                return { success: true, blockCount: updated.length };
              },

              // ── Global "For You" Feed ──
              // Queries ALL known wallets to build a true global feed like X's "For You"
              getForYouFeed: async (limit = 30, before = null) => {
                try {
                  const helius = getHeliusService();
                  if (!helius.isAvailable()) {
                    console.warn('⚠️ Helius not available for global feed');
                    return { success: true, beams: [], hasMore: false };
                  }

                  // Gather all known wallets
                  const knownWallets = new Set();
                  const myAddr = walletAdapter.publicKey.toString();
                  knownWallets.add(myAddr);

                  // From persistent registry
                  try {
                    const raw = localStorage.getItem('znoospace_known_wallets');
                    if (raw) {
                      const list = JSON.parse(raw);
                      if (Array.isArray(list)) list.forEach(w => knownWallets.add(w));
                    }
                  } catch {}

                  // From follow list
                  try {
                    const raw = secureStorage.getItem(`znoospace_following_${myAddr}`);
                    if (raw) {
                      const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
                      if (Array.isArray(list)) list.forEach(w => knownWallets.add(w));
                    }
                  } catch {}

                  // From profile index
                  try {
                    const profileRaw = secureStorage.getItem('znoospace_user_profiles');
                    if (profileRaw) {
                      const profiles = typeof profileRaw === 'string' ? JSON.parse(profileRaw) : profileRaw;
                      if (profiles && typeof profiles === 'object') {
                        Object.keys(profiles).forEach(w => knownWallets.add(w));
                      }
                    }
                  } catch {}

                  // Quick discovery: scan Memo program for recent Znoospace beams
                  try {
                    const MEMO_PROGRAM_ID = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');
                    const globalSigs = await Promise.race([
                      connection.getSignaturesForAddress(MEMO_PROGRAM_ID, { limit: 200 }),
                      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
                    ]);
                    const beamSigs = globalSigs.filter(sig =>
                      sig.memo && (sig.memo.includes('Znoospace Beam') || sig.memo.includes('ChirpX Beam'))
                    );
                    // Extract fee payers (authors) from signature info
                    for (const sig of beamSigs.slice(0, 50)) {
                      try {
                        const tx = await Promise.race([
                          connection.getTransaction(sig.signature, { maxSupportedTransactionVersion: 0 }),
                          new Promise((_, reject) => setTimeout(() => reject(), 2000))
                        ]);
                        if (tx) {
                          const keys = tx.transaction?.message?.accountKeys || tx.transaction?.message?.staticAccountKeys || [];
                          if (keys.length > 0) knownWallets.add(keys[0].toString());
                        }
                      } catch {}
                    }
                  } catch (e) {
                    console.warn('⚠️ Memo program scan failed, using known wallets only');
                  }

                  // Remove blocked users
                  const blocked = new Set(simpleService.getBlockedUsers());
                  const walletsToQuery = [...knownWallets].filter(w => !blocked.has(w));

                  // Save updated registry
                  localStorage.setItem('znoospace_known_wallets', JSON.stringify([...knownWallets]));
                  console.log(`🌐 For You: querying ${walletsToQuery.length} wallets (${blocked.size} blocked)`);

                  // Query all wallets via Helius in parallel
                  const perWalletLimit = Math.max(5, Math.ceil(limit / Math.max(walletsToQuery.length, 1)));
                  const results = await Promise.allSettled(
                    walletsToQuery.map(addr =>
                      helius.getBeamsForWallet(addr, { limit: perWalletLimit, before })
                    )
                  );

                  const allBeams = [];
                  for (const result of results) {
                    if (result.status === 'fulfilled' && result.value.beams) {
                      allBeams.push(...result.value.beams);
                    }
                  }

                  // Register any newly discovered authors
                  const newAuthors = allBeams.map(b => b.author).filter(a => a && a !== 'Unknown');
                  simpleService.registerWallets(newAuthors);

                  // Deduplicate by signature
                  const seen = new Set();
                  const unique = allBeams.filter(beam => {
                    if (seen.has(beam.signature)) return false;
                    seen.add(beam.signature);
                    return true;
                  });

                  // Sort: newest first (likes-based sorting happens in UI since likes are local)
                  unique.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

                  const paginated = unique.slice(0, limit);
                  const lastSig = paginated.length > 0 ? paginated[paginated.length - 1].signature : null;

                  console.log(`✅ For You feed: ${paginated.length} beams from ${walletsToQuery.length} wallets`);
                  return {
                    success: true,
                    beams: paginated,
                    hasMore: unique.length > limit,
                    nextCursor: lastSig,
                    totalFetched: unique.length
                  };
                } catch (error) {
                  console.error('❌ Error loading For You feed:', error);
                  return { success: false, beams: [], hasMore: false, error: error.message };
                }
              }
            };
            
            setSolanaService(simpleService);
            initializedForRef.current = walletAddress;
            console.log('✅ useZnoospaceProgram: Simple SolanaService initialized successfully');
          } else {
            console.log('❌ useZnoospaceProgram: No wallet available');
          }
        } catch (error) {
          console.error('❌ useZnoospaceProgram: Error initializing wallet:', error);
          setWallet(null);
          setSolanaService(null);
        }
      } else {
        console.log('🔧 useZnoospaceProgram: No wallet address provided, clearing state');
        initializedForRef.current = null;
        setWallet(null);
        setSolanaService(null);
        setUserProfile(null);
        setPosts([]);
      }
    };
    
    initializeWallet();
  }, [walletAddress, connection]);

  // Load user profile
  const loadUserProfile = useCallback(async () => {
    if (!wallet?.publicKey || !solanaService) return;

    try {
      setLoading(true);
      const result = await solanaService.getUserProfile(wallet.publicKey);
      if (result.success) {
        setUserProfile(result.data);
        return;
      }
    } catch (error) {
      console.error('Error loading user profile from blockchain:', error);
    }

    // NO FALLBACK - Profile must exist on blockchain
    console.log('❌ No user profile found on blockchain');
    setUserProfile(null);
    setLoading(false);
  }, [wallet?.publicKey, solanaService]);

  // Create user profile
  const createUserProfile = useCallback(async (username, displayName, bio = '', avatarUrl = '') => {
    if (!solanaService) {
      throw new Error('Wallet not connected');
    }

    try {
      setLoading(true);
      setError(null);
      const result = await solanaService.createUserProfile(username, displayName, bio, avatarUrl);
      
      if (result.success) {
        if (result.profile) {
          console.log('Setting user profile from fallback result:', result.profile);
          setUserProfile(result.profile);
        } else {
          await loadUserProfile();
        }
        return result;
      } else {
        throw new Error('Failed to create user profile');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [solanaService, loadUserProfile]);

  // Create post
  const createPost = useCallback(async (content, mediaUrls = []) => {
    if (!solanaService || !userProfile) {
      throw new Error('User profile required to create posts');
    }

    try {
      setLoading(true);
      setError(null);
      const result = await solanaService.createPost(content, mediaUrls, 'text');
      
      if (result.success) {
        await loadPosts();
        return result;
      } else {
        throw new Error('Failed to create post');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [solanaService, userProfile]);

  // Like post
  const likePost = useCallback(async (postPublicKey) => {
    if (!solanaService) {
      throw new Error('Wallet not connected');
    }

    try {
      setLoading(true);
      setError(null);
      const result = await solanaService.likePost(postPublicKey);
      
      if (result.success) {
        await loadPosts();
        return result;
      } else {
        throw new Error('Failed to like post');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [solanaService]);

  // Follow user
  const followUser = useCallback(async (targetUserPublicKey) => {
    if (!solanaService) {
      throw new Error('Wallet not connected');
    }

    try {
      setLoading(true);
      setError(null);
      const result = await solanaService.followUser(targetUserPublicKey);
      
      if (result.success) {
        await loadUserProfile();
        return result;
      } else {
        throw new Error('Failed to follow user');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [solanaService, loadUserProfile]);

  // Load posts (user's own posts or all posts)
  const loadPosts = useCallback(async (userPublicKey = null) => {
    if (!solanaService) return;

    try {
      setLoading(true);
      let result;
      
      if (userPublicKey) {
        result = await solanaService.getUserPosts(userPublicKey);
      } else {
        result = await solanaService.getAllPosts();
      }
      
      if (result.success) {
        setPosts(result.data);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [solanaService]);

  // Check if user has liked a post
  const hasLikedPost = useCallback(async (postPublicKey) => {
    if (!solanaService || !wallet?.publicKey) return false;

    try {
      const result = await solanaService.hasLikedPost(wallet.publicKey, postPublicKey);
      return result.hasLiked;
    } catch (error) {
      console.error('Error checking like status:', error);
      return false;
    }
  }, [solanaService, wallet?.publicKey]);

  // Check if user is following another user
  const isFollowing = useCallback(async (targetUserPublicKey) => {
    if (!solanaService || !wallet?.publicKey) return false;

    try {
      const result = await solanaService.isFollowing(wallet.publicKey, targetUserPublicKey);
      return result.isFollowing;
    } catch (error) {
      console.error('Error checking follow status:', error);
      return false;
    }
  }, [solanaService, wallet?.publicKey]);

  // Load posts when service is ready
  useEffect(() => {
    if (solanaService && userProfile) {
      loadPosts();
    }
  }, [solanaService, userProfile]);

  return {
    // State
    userProfile,
    posts,
    loading,
    error,
    isConnected: wallet?.connected || false,
    publicKey: wallet?.publicKey,
    
    // Services
    solanaService,
    wallet,
    
    // Methods
    createUserProfile,
    createPost,
    likePost,
    followUser,
    loadPosts,
    hasLikedPost,
    isFollowing,
    loadUserProfile
  };
};
